import { useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import MARK_NOTIFICATION_ASREAD from "../../graphql/mutations/UserNotifications/markNotificationAsRead";
import USER_NOTIFICATION from "../../graphql/queries/UserNotification";
import GET_USER_NOTIFICATION_EVENTS from "../../graphql/subscription/userNotificationsEvents";
import { AuthContext } from "../../providers/authProvider";

export const userNotificationContext =
  createContext(undefined);

const { Provider } = userNotificationContext;

const UserNotificationProvider = ({
  children,
}) => {

  const { isAuthenticated } = useContext(AuthContext);
  const accessToken =
    localStorage.getItem("access_token");

  const [notifications, setNotifications] =
    useState([]);

  const [unreadCount, setUnreadCount] =
    useState(0);

  const [totalCount, setTotalCount] =
    useState(0);

  // anv�nds f�r att veta om vi appendar eller ers�tter
  const [currentOffset, setCurrentOffset] =
    useState(0);

  // MUI Popover anchor
  const [anchorEl, setAnchorEl] =
    useState(null);

  const isOpen = Boolean(anchorEl);

  const hasMore =
    notifications.length < totalCount;

  const openPopup = event => {
    setAnchorEl(event.currentTarget);
  };

  const closePopup = () => {
    setAnchorEl(null);
  };

  const togglePopup = event => {
    if (anchorEl) {
      closePopup();
    } else {
      openPopup(event);
    }
  };

  const [markNotificationAsReadMutation] =
    useMutation(MARK_NOTIFICATION_ASREAD);

  const [
    fetchNotifications,
    { loading },
  ] = useLazyQuery(
    USER_NOTIFICATION,
    {
      fetchPolicy: "network-only",
      onCompleted: data => {
        const result =
          data?.userNotificationContext
            ?.notifications;

        if (!result) return;

        const fetchedNotifications =
          result.notifications || [];

        setNotifications(prev =>
          currentOffset === 0
            ? fetchedNotifications
            : [
              ...prev,
              ...fetchedNotifications,
            ]
        );

        setUnreadCount(
          result.unreadCount || 0
        );

        setTotalCount(
          result.totalCount || 0
        );
      },
    }
  );

  useEffect(() => {
    
    if (isAuthenticated()) {
      setCurrentOffset(0);
      fetchNotifications({
        variables: {
          offset: 0,
        },
      });
    }

  }, [isAuthenticated]);

  const loadMore = () => {
    const nextOffset =
      notifications.length;

    setCurrentOffset(nextOffset);

    fetchNotifications({
      variables: {
        offset: nextOffset,
      },
    });
  };

  const reloadNotifications = () => {
    setCurrentOffset(0);

    fetchNotifications({
      variables: {
        offset: 0,
      },
    });
  };

  const markAsRead = async (
    userNotificationId
  ) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.userNotificationId ===
          userNotificationId
          ? {
            ...notification,
            readAt:
              new Date().toISOString(),
          }
          : notification
      )
    );

    setUnreadCount(prev =>
      Math.max(prev - 1, 0)
    );

    try {
      await markNotificationAsReadMutation({
        variables: {
          userNotificationId,
        },
      });
    } catch (error) {
      console.error(
        "Failed to mark as read",
        error
      );
    }
  };

  const addNotification = notification => {
    let added = false;

    setNotifications(prev => {
      const exists = prev.some(
        item =>
          item.userNotificationId ===
          notification.userNotificationId
      );

      if (exists) {
        return prev;
      }

      added = true;

      return [
        notification,
        ...prev,
      ];
    });

    if (added) {
      setTotalCount(prev => prev + 1);

      if (!notification.readAt) {
        setUnreadCount(
          prev => prev + 1
        );
      }
    }
  };

  const shouldStart =
    isAuthenticated() && !!accessToken;

  useSubscription(
    GET_USER_NOTIFICATION_EVENTS,
    {
      skip: !shouldStart,
      variables: {
        authToken: accessToken,
      },
      onData: ({ data }) => {
        const notification =
          data?.data
            ?.userNotificationEvents;

        if (!notification) return;

        addNotification(notification);
      },
    }
  );

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      totalCount,
      hasMore,
      loading,
      anchorEl,
      isOpen,
      openPopup,
      closePopup,
      togglePopup,
      markAsRead,
      addNotification,
      loadMore,
      reloadNotifications,
    }),
    [
      notifications,
      unreadCount,
      totalCount,
      hasMore,
      loading,
      anchorEl,
      isOpen,
    ]
  );


  return (
    <Provider value={value}>
      {children}
    </Provider>
  );
};

export default UserNotificationProvider;